import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatCents } from '@affily/shared/money';
import { CURRENCY_LABELS } from '@affily/shared/constants';
import { api } from '../../lib/api.js';
import { useFetch } from '../../hooks/useFetch.js';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import { Button, Badge, Spinner, EmptyState, Alert, SectionTitle } from '../../components/ui.jsx';
import { HelpLink } from '../Help.jsx';

const statusTone = {
  paid: 'positive',
  processing: 'accent',
  pending: 'neutral',
  failed: 'negative',
  cancelled: 'neutral',
};

export default function CreatorPayouts() {
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const account = useFetch('/stripe/account');
  const balance = useFetch('/stripe/balance');
  const payouts = useFetch('/stripe/payouts?side=creator');

  async function connect() {
    setBusy(true);
    setError('');
    try {
      const { url } = await api.post('/stripe/connect');
      window.location.href = url;
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  const acct = account.data;
  const bal = balance.data?.balance;
  const rows = payouts.data?.payouts ?? [];

  return (
    <DashboardLayout
      side="creator"
      title="Payouts"
      description={
        <>
          Connect Stripe once, then commissions settle to your account.{' '}
          <HelpLink slug="how-do-i-get-paid">How and when you’re paid</HelpLink>
        </>
      }
    >
      <div className="space-y-8">
        {params.get('connected') === '1' && (
          <Alert tone="positive">
            You’re back from Stripe. If anything is still outstanding, it’s listed below.
          </Alert>
        )}
        <Alert>{error}</Alert>

        <section>
          <SectionTitle>Payout account</SectionTitle>
          {account.loading ? (
            <Spinner />
          ) : !acct?.stripeConfigured ? (
            <Alert tone="caution">
              Payouts aren’t configured on this server yet (no Stripe key). Your commissions still
              accrue and are recorded — nothing is lost.
            </Alert>
          ) : !acct.connected ? (
            <div className="rounded-lg border border-rule bg-paper-raised p-5">
              <p className="font-medium">No Stripe account connected</p>
              <p className="mt-1 text-sm text-ink-muted">
                Stripe handles identity verification and holds your bank details — Affily never sees
                them.
              </p>
              <Button onClick={connect} disabled={busy} className="mt-4">
                {busy ? 'Opening Stripe…' : 'Connect Stripe'}
              </Button>
            </div>
          ) : acct.payoutsEnabled ? (
            <div className="rounded-lg border border-positive/25 bg-positive-soft p-5">
              <p className="font-medium text-positive">Connected and ready</p>
              <p className="mt-1 text-sm text-ink-muted">
                Settled commissions transfer to this account.
              </p>
            </div>
          ) : (
            <div className="rounded-lg border border-caution/30 bg-caution-soft p-5">
              <p className="font-medium text-caution">Stripe still needs something from you</p>
              {acct.requirements?.length > 0 ? (
                <>
                  <p className="mt-1 text-sm text-ink-muted">
                    Until these are done, Stripe will refuse a transfer:
                  </p>
                  {/* Surfaced verbatim rather than hidden behind a generic
                      "pending" — otherwise a creator stares at a disabled
                      button with no idea what to do. */}
                  <ul className="mt-2 list-inside list-disc text-sm text-ink-muted">
                    {acct.requirements.map((r) => (
                      <li key={r}>
                        <code className="text-xs">{r}</code>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="mt-1 text-sm text-ink-muted">
                  Stripe is still reviewing your details.
                </p>
              )}
              <Button onClick={connect} disabled={busy} variant="outline" className="mt-4">
                {busy ? 'Opening Stripe…' : 'Finish onboarding'}
              </Button>
            </div>
          )}
        </section>

        {bal && (
          <section>
            <SectionTitle hint="Net of your share of Affily’s fee">What you’re owed</SectionTitle>
            {/* One block per currency: euros and dollars are paid separately, never added up. */}
            {(balance.data?.balances?.length > 1 ? balance.data.balances : [bal]).map((b) => (
            <div key={b.currency ?? 'usd'} className="mt-3 first:mt-0">
            {balance.data?.balances?.length > 1 && (
              <p className="mb-1.5 text-2xs font-semibold uppercase tracking-wider text-ink-faint">
                {CURRENCY_LABELS[b.currency] ?? b.currency}
              </p>
            )}
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-rule bg-rule lg:grid-cols-4">
              {[
                ['Accrued', b.pending, 'Tracked, not yet confirmed'],
                ['Approved', b.approved, 'Confirmed by the brand'],
                ['Ready to pay', b.settled, 'Queued for the next payout'],
                ['Paid out', b.paid, 'Already transferred'],
              ].map(([label, row, hint]) => (
                <div key={label} className="bg-paper-raised px-4 py-3">
                  <p className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
                    {label}
                  </p>
                  <p className="mt-1 text-xl font-semibold tabular-nums">
                    {formatCents(row.netCents, { currency: b.currency })}
                  </p>
                  <p className="mt-0.5 text-xs text-ink-faint">
                    {row.count} {row.count === 1 ? 'entry' : 'entries'} · {hint}
                  </p>
                </div>
              ))}
            </div>
            </div>
            ))}
          </section>
        )}

        <section>
          <SectionTitle>Payout history</SectionTitle>
          {payouts.loading ? (
            <Spinner />
          ) : rows.length === 0 ? (
            <EmptyState title="No payouts yet">
              Payouts appear once a brand settles commissions you’ve earned.
            </EmptyState>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th className="num">Commissions</th>
                    <th className="num">Gross</th>
                    <th className="num">Fee</th>
                    <th className="num">Received</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr key={p.id}>
                      <td className="whitespace-nowrap text-ink-muted">
                        {new Date(p.paidAt ?? p.createdAt).toLocaleDateString()}
                      </td>
                      <td className="num">{p.commissionCount}</td>
                      <td className="num">{formatCents(p.grossCents, { currency: p.currency })}</td>
                      <td className="num text-ink-muted">−{formatCents(p.platformFeeCents, { currency: p.currency })}</td>
                      <td className="num font-medium">{formatCents(p.netCents, { currency: p.currency })}</td>
                      <td>
                        <Badge tone={statusTone[p.status]}>{p.status}</Badge>
                        {p.failureReason && (
                          <span className="block text-xs text-negative">{p.failureReason}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}
